import React, { useEffect, useState } from "react";
import PayrollRomms from "../../payroll/PayrollRomms";

interface RevenueFromOperationsRommsProps {
  onBack: () => void;
}

interface RommItem {
  id: string;
  risk: string;
  assertion: string;
  options: string[];
  workspace?: string | string[];
}

const BASE_OPTIONS = ["Lower", "Higher", "Significant", "NRPMM"];

const MODEL_TO_ROMMS: Record<string, RommItem[]> = {
  "Revenue - ship and bill": [
    { id: "RFO.SB.001", risk: "Revenue is recorded at the incorrect amount.", assertion: "Accuracy", options: BASE_OPTIONS, workspace: "Revenue from Operations" },
    { id: "RFO.SB.002", risk: "Revenue is incomplete.", assertion: "Completeness", options: BASE_OPTIONS, workspace: "Revenue from Operations" },
    { id: "RFO.SB.003", risk: "Revenue recorded does not represent actual transactions that occurred.", assertion: "Occurrence", options: BASE_OPTIONS, workspace: "Revenue from Operations" },
    { id: "RFO.SB.004", risk: "Revenue is not classified appropriately.", assertion: "Classification", options: BASE_OPTIONS, workspace: "Revenue from Operations" },
    { id: "RFO.SB.005", risk: "Revenue is recorded at incorrect period.", assertion: "Cutoff", options: BASE_OPTIONS, workspace: "Revenue from Operations" },
    { id: "RFO.SB.006", risk: "Revenue is overstated because credit notes issued post balance sheet date have been accounted for in the incorrect period.", assertion: "Cutoff, Occurrence", options: BASE_OPTIONS, workspace: "Revenue from Operations" },
    { id: "RFO.SB.007", risk: "Revenue is misstated because the estimates related to revenue are not appropriately estimated/recorded.", assertion: "Accuracy, Completeness", options: BASE_OPTIONS, workspace: "Revenue from Operations" },
  ],
  "Revenue - recognized over time - cost plus (Services)": [
    { id: "RFO.CP.001", risk: "Revenue is recorded at the incorrect amount.", assertion: "Accuracy", options: BASE_OPTIONS, workspace: "Revenue from Operations" },
    { id: "RFO.CP.002", risk: "Revenue is incomplete.", assertion: "Completeness", options: BASE_OPTIONS, workspace: "Revenue from Operations" },
    { id: "RFO.CP.003", risk: "Revenue recorded does not represent actual transactions that occurred.", assertion: "Occurrence", options: BASE_OPTIONS, workspace: "Revenue from Operations" },
    { id: "RFO.CP.004", risk: "Revenue is not classified appropriately.", assertion: "Classification", options: BASE_OPTIONS, workspace: "Revenue from Operations" },
    { id: "RFO.CP.005", risk: "Revenue is recorded at incorrect period.", assertion: "Cutoff", options: BASE_OPTIONS, workspace: "Revenue from Operations" },
    { id: "RFO.CP.006", risk: "Revenue is not recorded in proper account.", assertion: "Classification", options: BASE_OPTIONS, workspace: "Revenue from Operations" },
    { id: "RFO.CP.007", risk: "Recharged cost revenue is (1) not complete (2) not recorded in correct period (3) invalid (4) recorded at incorrect amount (5) incorrectly recorded as gross or net.", assertion: "Completeness, Cutoff, Accuracy, Occurrence, Classification", options: BASE_OPTIONS, workspace: "Revenue from Operations" },
  ],
  "Revenue - recognized over time - time elapsed (Services)": [
    { id: "RFO.TE.001", risk: "Revenue is recorded at the incorrect amount.", assertion: "Accuracy", options: BASE_OPTIONS, workspace: "Revenue from Operations" },
    { id: "RFO.TE.002", risk: "Revenue is incomplete.", assertion: "Completeness", options: BASE_OPTIONS, workspace: "Revenue from Operations" },
    { id: "RFO.TE.003", risk: "Revenue recorded does not represent actual transactions that occurred.", assertion: "Occurrence", options: BASE_OPTIONS, workspace: "Revenue from Operations" },
    { id: "RFO.TE.004", risk: "Revenue is not classified appropriately.", assertion: "Classification", options: BASE_OPTIONS, workspace: "Revenue from Operations" },
    { id: "RFO.TE.005", risk: "Revenue is recorded at incorrect period.", assertion: "Cutoff", options: BASE_OPTIONS, workspace: "Revenue from Operations" },
    { id: "RFO.TE.006", risk: "Revenue is not recorded in proper account.", assertion: "Classification", options: BASE_OPTIONS, workspace: "Revenue from Operations" },
  ],
  "Revenue - recognized over time - cost to cost (Services)": [
    { id: "RFO.CTC.001", risk: "Revenue is recorded at the incorrect amount.", assertion: "Accuracy", options: BASE_OPTIONS, workspace: "Revenue from Operations" },
    { id: "RFO.CTC.002", risk: "Revenue is incomplete.", assertion: "Completeness", options: BASE_OPTIONS, workspace: "Revenue from Operations" },
    { id: "RFO.CTC.003", risk: "Revenue recorded does not represent actual transactions that occurred.", assertion: "Occurrence", options: BASE_OPTIONS, workspace: "Revenue from Operations" },
    { id: "RFO.CTC.004", risk: "Revenue is not classified appropriately.", assertion: "Classification", options: BASE_OPTIONS, workspace: "Revenue from Operations" },
    { id: "RFO.CTC.005", risk: "Revenue is recorded at incorrect period.", assertion: "Cutoff", options: BASE_OPTIONS, workspace: "Revenue from Operations" },
    { id: "RFO.CTC.006", risk: "Revenue is not recorded in proper account.", assertion: "Classification", options: BASE_OPTIONS, workspace: "Revenue from Operations" },
  ],
};

function dedupeRomms(romms: RommItem[]): RommItem[] {
  const byRisk = new Map<string, RommItem>();
  romms.forEach((item) => {
    const key = `${item.risk}__${item.assertion}`;
    if (!byRisk.has(key)) byRisk.set(key, item);
  });
  return Array.from(byRisk.values());
}

export default function RevenueFromOperationsRomms({ onBack }: RevenueFromOperationsRommsProps) {
  const [romms, setRomms] = useState<RommItem[]>([]);

  useEffect(() => {
    const loadTailoringSelections = async () => {
      try {
        const res = await (window as any)?.sharePointAPI?.downloadClientJson?.(
          "Execution_TailoringQuestions_Responses.json",
        );
        const selectedRaw =
          res?.success &&
          res?.data &&
          res.data["Revenue from Operations"] &&
          res.data["Revenue from Operations"]["rfo-model-selection"]?.selectedOption
            ? String(
                res.data["Revenue from Operations"]["rfo-model-selection"].selectedOption,
              )
            : "";

        const selectedModels = selectedRaw
          .split(",")
          .map((value: string) => value.trim())
          .filter(Boolean);

        const loaded = selectedModels.flatMap((model: string) => MODEL_TO_ROMMS[model] || []);
        setRomms(dedupeRomms(loaded));
      } catch (error) {
        console.warn("[RFO] Failed to load tailoring selections for ROMMs", error);
        setRomms([]);
      }
    };

    loadTailoringSelections();
  }, []);

  return (
    <PayrollRomms
      onBack={onBack}
      onComplete={() => {}}
      workspace="Revenue from Operations"
      rommsOverride={romms}
    />
  );
}
